/**
 * Hjelpemetoder for Perioder basert på ZonedDateTime
 */

import {Temporal} from "temporal-polyfill";
import {lesZonedDateTimeSerialisert, lesZonedDateTimeSerialisertOpt, skrivZonedDateTimeSerialisert, skrivZonedDateTimeSerialisertOpt} from "./temporal";
import {erNil} from "./nilsjekk";
import {DatoPeriode, ZonedDateTimeSerialisert} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {nil} from "felles-ng2/typer/nullish";
import ZonedDateTime = Temporal.ZonedDateTime;
import ZonedDateTimeLike = Temporal.ZonedDateTimeLike;
import {Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {ZonedDateTimePeriodeMuligÅpenSlutt as _ZonedDateTimePeriodeMuligÅpenSlutt, ZonedDateTimePeriodeLukket, ZonedDateTimePeriodeLukketSerialisert} from "felles-ng2/typer/ZonedDateTimePeriode";

/** @deprecated bruk ZonedDateTimePeriodeLukket fra src/scripts/felles-ng2/typer/ZonedDateTimePeriode.ts */
export type ZonedDateTimePeriode = { start: ZonedDateTime, slutt: ZonedDateTime };
/** @deprecated bruk ZonedDateTimePeriodeMuligÅpenSlutt fra src/scripts/felles-ng2/typer/ZonedDateTimePeriode.ts */
export type ZonedDateTimePeriodeMuligÅpenSlutt = { start: ZonedDateTime, slutt: ZonedDateTime | null };
/** @deprecated bruk ZonedDateTimePeriodeLukketSerialisert fra src/scripts/felles-ng2/typer/ZonedDateTimePeriode.ts */
export type DatoPeriodeLukket = ZonedDateTimePeriodeLukketSerialisert;

/** Deserialiserer til ZonedDateTime fra json-representasjon sendt fra server
 * @deprecated bruke ZonedDateTimePeriodeMuligÅpenSlutt.les */
export function lesDatoPeriodeMuligÅpenSlutt(periode: DatoPeriode<any>, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): _ZonedDateTimePeriodeMuligÅpenSlutt {
    return _ZonedDateTimePeriodeMuligÅpenSlutt.les(periode, tidssone);
}

/** Deserialiserer til ZonedDateTime fra json-representasjon sendt fra server
 * @deprecated bruke ZonedDateTimePeriodeMuligÅpenSlutt.lesOpt */
export function lesDatoPeriodeMuligÅpenSluttOpt(periode: DatoPeriode<any> | nil, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): _ZonedDateTimePeriodeMuligÅpenSlutt | null {
    return _ZonedDateTimePeriodeMuligÅpenSlutt.lesOpt(periode, tidssone);
}

/** Deserialiserer til ZonedDateTime fra json-representasjon sendt fra server
 * @deprecated bruke ZonedDateTimePeriodeLukket.les */
export function lesDatoPeriode(periode: DatoPeriode<any>, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTimePeriodeLukket {
    return ZonedDateTimePeriodeLukket.les(periode as ZonedDateTimePeriodeLukketSerialisert, tidssone);
}

/** Deserialiserer til ZonedDateTime fra json-representasjon sendt fra server
 * @deprecated bruke ZonedDateTimePeriodeLukket.lesOpt */
export function lesDatoPeriodeOpt(periode: DatoPeriode<any> | nil, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTimePeriodeLukket | null {
    return ZonedDateTimePeriodeLukket.lesOpt(periode as ZonedDateTimePeriodeLukketSerialisert, tidssone);
}

/** Serialiserer til streng fra ZonedDateTime
 * @deprecated bruke ZonedDateTimePeriode.serialiser */
export function skrivDatoPeriodeMuligÅpenSlutt(periode: ZonedDateTimePeriodeMuligÅpenSlutt): DatoPeriode<any> {
    return {start: skrivZonedDateTimeSerialisert(periode.start), slutt: skrivZonedDateTimeSerialisertOpt(periode.slutt)};
}

/** Serialiserer til streng fra ZonedDateTime
 * @deprecated bruke ZonedDateTimePeriode.serialiser */
export function skrivDatoPeriodeMuligÅpenSluttOpt(periode: ZonedDateTimePeriodeMuligÅpenSlutt | nil): DatoPeriode<any> | null {
    if (erNil(periode)) return null;
    return skrivDatoPeriodeMuligÅpenSlutt(periode);
}

/** Serialiserer til streng fra ZonedDateTime
 * @deprecated bruke ZonedDateTimePeriode.serialiser  */
export function skrivDatoPeriode(periode: ZonedDateTimePeriode): DatoPeriodeLukket {
    return {start: skrivZonedDateTimeSerialisert(periode.start), slutt: skrivZonedDateTimeSerialisert(periode.slutt)};
}

/** Serialiserer til streng fra ZonedDateTime
 * @deprecated bruke ZonedDateTimePeriode.serialiser */
export function skrivDatoPeriodeOpt(periode: ZonedDateTimePeriode | nil): DatoPeriodeLukket | null {
    if (erNil(periode)) return null;
    return skrivDatoPeriode(periode);
}


/** Returnerer antall kalenderdager i angitt periode.
 * Dvs (antall 'midnatt' man passerer mellom start og slutt) + 1.
 * Merk at beregningen gjøres ihht den tidssone som er angitt i start.
 *  @deprecated bruk ZonedDateTimePeriodeLukket.antallDager */
export function antallDagerIPeriode(periode: ZonedDateTimePeriode): number {
    return periode.start.toPlainDate()
      .until(periode.slutt.withTimeZone(periode.start.getTimeZone()).toPlainDate())
      .total({unit: 'days'}) + 1;
}

/** @deprecated bruk ZonedDateTimePeriodeLukket.antallMinutter */
export function antallMinutterIPeriode(periode: ZonedDateTimePeriode): number {
    return periode.start
      .until(periode.slutt)
      .total({unit: 'minutes'});
}

/** @deprecated bruk ZonedDateTimePeriodeLukket.inneholder */
export function periodeInneholder(periode: ZonedDateTimePeriodeMuligÅpenSlutt, dato: ZonedDateTimeLike): boolean {
    if (ZonedDateTime.compare(periode.start, dato) > 0) return false;
    if (periodeHarIkkeÅpenSlutt(periode) && ZonedDateTime.compare(periode.slutt!, dato) < 0) return false;
    return true;
}

/** @deprecated bruk ZonedDateTimePeriodeLukket.inneholderNå */
export function periodeInneholderNå(periode: ZonedDateTimePeriodeMuligÅpenSlutt, klokke: Klokke): boolean {
    return periodeInneholder(periode, klokke.nåSomZonedDateTime());
}

/** @deprecated bruk ZonedDateTimePeriode.harÅpenSlutt */
export function periodeHarÅpenSlutt(periode: ZonedDateTimePeriodeMuligÅpenSlutt): boolean {
    return erNil(periode.slutt);
}

/** @deprecated bruk ZonedDateTimePeriode.harLukketSlutt */
export function periodeHarIkkeÅpenSlutt(periode: ZonedDateTimePeriodeMuligÅpenSlutt): boolean {
    return !periodeHarÅpenSlutt(periode);
}

/** @deprecated bruk ZonedDateTimePeriodeLukket.slutterFør */
export function periodeSlutterFør(periode: ZonedDateTimePeriodeMuligÅpenSlutt | null, dato: ZonedDateTimeLike): boolean {
    if (erNil(periode)) return false;
    if (periodeHarÅpenSlutt(periode)) return false;
    return ZonedDateTime.compare(periode.slutt!, dato) < 0;
}

/** @deprecated bruk ZonedDateTimePeriodeLukket.erPassert */
export function periodeErPassert(periode: ZonedDateTimePeriodeMuligÅpenSlutt | null, klokke: Klokke): boolean {
    return periodeSlutterFør(periode, klokke.nåSomZonedDateTime());
}

/** @deprecated bruk ZonedDateTimePeriodeLukket.erPassert */
export function periodeOverlapper(a: ZonedDateTimePeriodeMuligÅpenSlutt, b: ZonedDateTimePeriodeMuligÅpenSlutt): boolean {
    return periodeInneholder(a, b.start) || periodeInneholder(b, a.start);
}
